import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, Activity, TrendingUp, CalendarClock, Rocket,
  Target, Scale, TriangleAlert,
} from 'lucide-react';
import api from '../../services/api';
import { getSocket } from '../../services/socket';
import TradeTable from '../../components/TradeTable';
import ContainerBadge from '../../components/ContainerBadge';
import KPITile from '../../components/KPITile';

const plain = n => `$${Number(n || 0).toFixed(2)}`;
const money = n => `${n >= 0 ? '+' : '−'}$${Math.abs(Number(n || 0)).toFixed(2)}`;
const intentOf = n => (n > 0 ? 'profit' : n < 0 ? 'loss' : 'neutral');
const trendOf  = n => (n > 0 ? 'up' : n < 0 ? 'down' : 'flat');

const DEPLOYMENT_BADGE = {
  deployed: 'badge-success',
  stopped:  'badge-secondary',
  error:    'badge-danger',
};

export default function CustomerOverview() {
  const [dash, setDash]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId]   = useState(null);
  const [actionError, setActionError] = useState('');

  function refresh() {
    return api.get('/customer/dashboard').then(r => setDash(r.data));
  }

  async function handlePause(dep) {
    if (!window.confirm(
      `Pause "${dep.eaName}" on ${dep.symbol}? The EA is detached from its chart ` +
      `and your MT5 terminal restarts. You can resume it anytime.`)) return;
    setActionError('');
    setBusyId(dep._id);
    try {
      await api.post(`/deployments/${dep._id}/stop`);
      await refresh();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Pause failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleResume(dep) {
    if (!window.confirm(
      `Resume "${dep.eaName}" on ${dep.symbol}? This restarts your MT5 terminal.`)) return;
    setActionError('');
    setBusyId(dep._id);
    try {
      await api.post(`/deployments/${dep._id}/resume`);
      await refresh();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Resume failed');
    } finally {
      setBusyId(null);
    }
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));

    const sock = getSocket();
    if (sock) {
      sock.on('account:update', (data) => {
        setDash(prev => prev
          ? { ...prev, account: { ...prev.account, balance: data.balance, equity: data.equity } }
          : prev);
      });
      sock.on('trade:opened', refresh);
      sock.on('trade:closed', refresh);
      sock.on('container:status', (data) => {
        setDash(prev => prev ? { ...prev, containerStatus: data.status } : prev);
      });
    }
  }, []);

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading dashboard…</p>;

  const acct = dash?.account || {};
  const openTrades = dash?.openTrades || [];
  const deployments = dash?.deployments || [];
  const running = deployments.filter(d => d.status === 'deployed').length;
  const openPnl  = acct.openPnl  ?? 0;
  const todayPnl = acct.todayPnl ?? 0;

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Customer</span>
          <h1>Overview</h1>
          <div className="subtitle">Your account, your deployed EAs, and live positions</div>
        </div>
        <ContainerBadge status={dash?.containerStatus} />
      </div>

      <div className="row">
        <Tile><KPITile label="Balance" value={plain(acct.balance)} sub="Account balance"
          icon={<Wallet size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Equity" value={plain(acct.equity)} sub="Incl. floating P&L"
          icon={<Activity size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Open P&L" value={money(openPnl)} sub={openPnl >= 0 ? 'Floating profit' : 'Floating loss'}
          icon={<TrendingUp size={16} />} intent={intentOf(openPnl)} trend={trendOf(openPnl)} /></Tile>
        <Tile><KPITile label="Today P&L" value={money(todayPnl)} sub={todayPnl >= 0 ? 'In profit' : 'In loss'}
          icon={<CalendarClock size={16} />} intent={intentOf(todayPnl)} trend={trendOf(todayPnl)} /></Tile>

        <Tile><KPITile label="Running EAs" value={running} sub={`${deployments.length} total deployments`}
          icon={<Rocket size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Win Rate" value={`${dash?.stats?.winRate ?? 0}%`} sub={`${dash?.stats?.totalTrades ?? 0} total trades`}
          icon={<Target size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Profit Factor" value={dash?.stats?.profitFactor ?? 0} sub="Gross profit ÷ loss"
          icon={<Scale size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Max Drawdown" value={`${dash?.stats?.maxDrawdown ?? 0}%`} sub="Peak-to-trough"
          icon={<TriangleAlert size={16} />} intent="warning" /></Tile>
      </div>

      <div className="row">
        <div className="col-lg-5 mb-4">
          <div className="card" style={{ height: '100%' }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 className="card-title">Deployed EAs</h5>
                <div className="card-category" style={{ marginTop: 4 }}>{running} running</div>
              </div>
              <Rocket size={16} color="var(--text-tertiary)" />
            </div>
            <div className="card-body">
              {actionError && <div className="alert alert-danger">{actionError}</div>}
              {deployments.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>
                  No EAs deployed yet. <Link to="/customer/eas">Upload and deploy your first EA →</Link>
                </p>
              ) : (
                <div className="table-responsive table-cards table-cards-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>EA</th><th>Symbol</th><th>TF</th><th>Status</th><th className="text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                      {deployments.map(d => (
                        <tr key={d._id}>
                          <td data-label="EA"><strong>{d.eaName}</strong></td>
                          <td data-label="Symbol">{d.symbol}</td>
                          <td data-label="TF" className="num">{d.timeframe}</td>
                          <td data-label="Status">
                            <span className={`badge ${DEPLOYMENT_BADGE[d.status] || 'badge-secondary'}`}>
                              {d.status === 'stopped' ? 'paused' : d.status}
                            </span>
                          </td>
                          <td data-label="Actions" className="text-right">
                            {d.status === 'deployed' ? (
                              <button className="btn btn-sm btn-outline-warning"
                                disabled={busyId === d._id}
                                onClick={() => handlePause(d)}>
                                {busyId === d._id ? 'Pausing…' : 'Pause'}
                              </button>
                            ) : (
                              <button className="btn btn-sm btn-outline-success"
                                disabled={busyId === d._id}
                                onClick={() => handleResume(d)}>
                                {busyId === d._id ? 'Resuming…' : 'Resume'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-7 mb-4">
          <div className="card" style={{ height: '100%' }}>
            <div className="card-header">
              <h5 className="card-title">Open Positions</h5>
              <div className="card-category" style={{ marginTop: 4 }}>{openTrades.length} live</div>
            </div>
            <div className="card-body">
              <TradeTable trades={openTrades} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const Tile = ({ children }) => <div className="col-lg-3 col-md-6 col-6 mb-4">{children}</div>;
